import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography, IconButton, TextField } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import WhatshotIcon from '@mui/icons-material/Whatshot';
import {
  DragIndicator,
  Edit as EditIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Lock,
} from '@mui/icons-material';
import { EvohomeDevice, evohomeControl } from '../../services/evohomeApi';
import DeviceTypeImage from '../shared/DeviceTypeImage';
import { DeviceIconWithBrandChip } from '../shared/DeviceBrandChip';
import CardStatusPowerChrome, { POWER_ICON_FONT_SIZE } from '../shared/CardStatusPowerChrome';
import { POWER_BUTTON_SIZE } from '../../styles/deviceStyles';
import { artifactTypeLineSx, sonosCardOuterSx } from '../../installed/music';
import { useAiDevicePolicy, useShowAiCardBadge } from '../../contexts/AiDevicePolicyContext';
import { requirePinIfNeeded } from '../../services/pinGate';
import { EVOHOME_SETPOINT_DEBOUNCE_MS } from './evohomeCmdGuard';
import { isHeatingOff } from './evohomeModeUtils';

const ACCENT = '#2391FF';
const HEAT = '#FF8A65';
/** Naranja pleno por defecto si el artefacto no define color. */
const HEAT_ACTIVE = '#FF5722';
const CARD_BG = '#2F3235';
const PRESS = 'transform 120ms ease, background-color 140ms ease, border-color 140ms ease, color 140ms ease';
const SETPOINT_DEBOUNCE_MS = EVOHOME_SETPOINT_DEBOUNCE_MS;

const VALUE_CHIP_SX = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  px: 0.35,
  py: 0,
  borderRadius: '8px',
  bgcolor: 'rgba(0,0,0,0.28)',
  border: '1px solid rgba(255,255,255,0.08)',
  flexShrink: 0,
  height: 22,
  boxSizing: 'border-box',
} as const;

const STEP_CHIP_SX = {
  ...VALUE_CHIP_SX,
  gap: 0.2,
  ml: 1.25,
  justifyContent: 'flex-start',
} as const;

const STEP_BTN_SX = {
  p: 0.15,
  color: '#fff',
  borderRadius: '6px',
  transition: PRESS,
  WebkitTapHighlightColor: 'transparent',
  '&:hover:not(.Mui-disabled)': { bgcolor: 'rgba(255,255,255,0.08)' },
  '&:active:not(.Mui-disabled)': { transform: 'scale(0.88)' },
  '&.Mui-disabled': { color: '#666' },
} as const;

const STEP_VALUE_SX = {
  minWidth: 26,
  textAlign: 'center' as const,
  fontSize: 13,
  fontWeight: 750,
  color: '#fff',
  letterSpacing: '-0.02em',
  lineHeight: 1,
};

interface Props {
  device: EvohomeDevice;
  onOpenModal?: (device: EvohomeDevice) => void;
  onUpdated?: (d: EvohomeDevice) => void;
  interactionLocked?: boolean;
  editMode?: boolean;
  isRoomEditing?: boolean;
  isEditingName?: boolean;
  dragHandleProps?: Record<string, unknown>;
  onStartEditName?: () => void;
  onSaveEditName?: (newName: string) => void;
  onCancelEditName?: () => void;
  showBrandChip?: boolean;
}

const EvohomeDeviceCard: React.FC<Props> = ({
  device,
  onOpenModal,
  onUpdated,
  interactionLocked = false,
  editMode = false,
  isRoomEditing = false,
  isEditingName = false,
  dragHandleProps,
  onStartEditName,
  onSaveEditName,
  onCancelEditName,
  showBrandChip = false,
}) => {
  const { t } = useTranslation();
  const showAiCardBadge = useShowAiCardBadge();
  const aiPolicy = useAiDevicePolicy();
  const aiWhitelisted = showAiCardBadge && aiPolicy.isEvohomeWhitelistedForIcon(device.id);
  const aiBlacklisted = showAiCardBadge && aiPolicy.isEvohomeBlacklistedForIcon(device.id);
  const locked = interactionLocked || isRoomEditing || isEditingName;
  const canControl = Boolean(device.can_control) && !locked && !editMode;
  const offline = !device.online;
  const hasArtefactoIcon = Boolean(device.artefacto?.nombre || device.artefacto_id);
  const artefactoNombre = device.artefacto?.nombre || null;
  const tipoArtefacto = artefactoNombre
    ?? t('evohome.card.fallbackZone', { defaultValue: 'Zona' });
  const [editingNameValue, setEditingNameValue] = useState(device.nombre);
  const [localSetpoint, setLocalSetpoint] = useState<number | null>(null);
  const setpointDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingSetpointRef = useRef<number | null>(null);
  const setpointSeqRef = useRef(0);
  const onUpdatedRef = useRef(onUpdated);
  onUpdatedRef.current = onUpdated;
  const deviceIdRef = useRef(device.id);
  deviceIdRef.current = device.id;

  const minSp = device.min_setpoint ?? 5;
  const maxSp = device.max_setpoint ?? 35;
  const askingHeat = Boolean(device.asking_heat);

  const flushSetpoint = useCallback(async (next: number) => {
    const seq = ++setpointSeqRef.current;
    try {
      // ± desde tarjeta: TemporaryOverride 60 min (evita PermanentOverride spam / mails TCC).
      const mode = String(device.setpoint_mode || '').toLowerCase().replace(/-/g, '');
      const isPermanent = mode.includes('permanent');
      const value = isPermanent
        ? next
        : { temperature: next, duration_minutes: 60 };
      const res = await evohomeControl(deviceIdRef.current, 'setpoint', value);
      if (seq !== setpointSeqRef.current) return;
      if (res?.device) onUpdatedRef.current?.(res.device);
    } catch {
      if (seq !== setpointSeqRef.current) return;
      setLocalSetpoint(null);
    }
  }, [device.setpoint_mode]);

  useEffect(() => {
    if (localSetpoint == null) return;
    if (pendingSetpointRef.current != null) return;
    if (device.setpoint != null && Math.abs(Number(device.setpoint) - localSetpoint) < 0.05) {
      setLocalSetpoint(null);
    }
  }, [device.setpoint, localSetpoint]);

  useEffect(() => () => {
    if (setpointDebounceRef.current) clearTimeout(setpointDebounceRef.current);
  }, []);

  useEffect(() => {
    if (isEditingName) setEditingNameValue(device.nombre);
  }, [isEditingName, device.id, device.nombre]);

  const executeControl = async (action: string, value?: unknown) => {
    if (!canControl) return;
    if (offline) return;
    if (action === 'setpoint') {
      const next = Math.min(maxSp, Math.max(minSp, Number(value)));
      if (!Number.isFinite(next)) return;
      setLocalSetpoint(next);
      pendingSetpointRef.current = next;
      if (setpointDebounceRef.current) clearTimeout(setpointDebounceRef.current);
      setpointDebounceRef.current = setTimeout(() => {
        setpointDebounceRef.current = null;
        const val = pendingSetpointRef.current;
        pendingSetpointRef.current = null;
        if (val != null) void flushSetpoint(val);
      }, SETPOINT_DEBOUNCE_MS);
      return;
    }
    try {
      const res = await evohomeControl(device.id, action, value);
      if (res?.device) onUpdated?.(res.device);
    } catch { /* upstream */ }
  };

  const run = async (e: React.MouseEvent, action: string, value?: unknown) => {
    e.stopPropagation();
    e.preventDefault();
    if (!requirePinIfNeeded(device.pin_required, device.id, () => {
      void executeControl(action, value);
    })) return;
    await executeControl(action, value);
  };

  const displaySp = localSetpoint != null
    ? localSetpoint
    : (device.setpoint != null ? Number(device.setpoint) : null);
  const heatingLocked = isHeatingOff(device.system_mode);
  const spDisabled = !canControl || offline || heatingLocked;
  const step = 0.5;
  // Misma visibilidad/posición que PowerButton en Samsung/LG (sin ser accionable).
  const showFlame = !editMode && !locked && !isRoomEditing;
  const flameActiveColor = (device.artefacto?.color_icono || '').trim() || HEAT_ACTIVE;

  return (
    <Box
      sx={{
        ...sonosCardOuterSx,
        backgroundColor: CARD_BG,
        opacity: 1,
        border: device.missing_from_cloud ? '1px solid #e5a54b' : 'none',
        overflow: 'visible',
        pointerEvents: 'none',
        '& button, & .MuiIconButton-root, & .MuiButton-root, & [role="button"], & .MuiCheckbox-root, & .MuiInput-root, & [data-device-icon]': {
          pointerEvents: 'auto',
          touchAction: isRoomEditing ? 'none' : 'manipulation',
        },
      }}
    >
      {!isRoomEditing && !isEditingName && (
        <Box
          onClick={(e) => {
            if (editMode || locked) return;
            const target = e.target as HTMLElement;
            if (
              target.closest('button')
              || target.closest('.MuiIconButton-root')
              || target.closest('.MuiCheckbox-root')
              || target.closest('[data-device-icon]')
            ) {
              return;
            }
            const scrolled = (window as any).dashboardHasScrolledRef?.current;
            if (!scrolled) onOpenModal?.(device);
          }}
          sx={{
            position: 'absolute',
            top: 0,
            left: '80px',
            // Incluye la flama (no es power accionable): todo el cuerpo abre el modal.
            right: 0,
            bottom: 0,
            pointerEvents: 'auto',
            touchAction: 'pan-y',
            zIndex: 1,
            cursor: 'pointer',
          }}
        />
      )}
      <DeviceIconWithBrandChip
        brand="evohome"
        showBrandChip={showBrandChip}
        sx={{ marginRight: 1 }}
      >
        <Box data-device-icon sx={{ flexShrink: 0, touchAction: 'pan-y', pointerEvents: 'auto' }}>
          {hasArtefactoIcon ? (
            <DeviceTypeImage
              tipo="EvoHome"
              tipo_artefacto={tipoArtefacto}
              iconOverride={device.artefacto?.icono ?? null}
              colorOverride={device.artefacto?.color_icono ?? ACCENT}
            />
          ) : (
            <Box sx={{
              width: 60, height: 60, borderRadius: '50%', bgcolor: '#1B1D21',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <WhatshotIcon sx={{ fontSize: 28, color: HEAT, opacity: 0.55 }} />
            </Box>
          )}
        </Box>
      </DeviceIconWithBrandChip>

      <Box sx={{
        flex: 1,
        minWidth: 0,
        overflow: 'hidden',
        transform: 'translateY(-2px)',
        pr: (showFlame || (editMode && !isRoomEditing)) ? '58px' : 0,
      }}>
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          marginBottom: 0.15,
          position: 'relative',
          zIndex: isEditingName ? 100 : 'auto',
        }}>
          {isEditingName ? (
            <>
              <TextField
                value={editingNameValue}
                onChange={(e) => setEditingNameValue(e.target.value)}
                variant="standard"
                autoFocus
                onClick={(e) => e.stopPropagation()}
                sx={{
                  flex: 1,
                  '& .MuiInput-root': { color: 'white', fontSize: 14, fontWeight: 'bold' },
                  '& .MuiInput-underline:before': { borderBottomColor: '#666' },
                  '& .MuiInput-underline:after': { borderBottomColor: '#2391FF' },
                }}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') onSaveEditName?.(editingNameValue);
                }}
              />
              <IconButton size="small" onClick={(e) => { e.stopPropagation(); onSaveEditName?.(editingNameValue); }} sx={{ padding: 1, marginLeft: 0.5, minWidth: 32, minHeight: 32, zIndex: 101, backgroundColor: 'rgba(35, 145, 255, 0.1)' }}>
                <CheckIcon sx={{ fontSize: 18, color: '#2391FF' }} />
              </IconButton>
              <IconButton size="small" onClick={(e) => { e.stopPropagation(); onCancelEditName?.(); }} sx={{ padding: 1, marginLeft: 0.5, minWidth: 32, minHeight: 32, zIndex: 101, backgroundColor: 'rgba(255, 68, 68, 0.1)' }}>
                <CloseIcon sx={{ fontSize: 18, color: '#ff4444' }} />
              </IconButton>
            </>
          ) : (
            <>
              <Typography sx={{ fontSize: 14, fontWeight: 'bold', color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
                {device.nombre}
              </Typography>
              {editMode && !isRoomEditing && onStartEditName && (
                <IconButton size="small" onClick={(e) => { e.stopPropagation(); onStartEditName(); }} sx={{ padding: 0.25, marginLeft: 0.5, flexShrink: 0, '&:hover': { backgroundColor: 'rgba(35, 145, 255, 0.1)' } }}>
                  <EditIcon sx={{ fontSize: 14, color: '#2391FF' }} />
                </IconButton>
              )}
            </>
          )}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0, mt: 0.1, minWidth: 0, height: 22 }}>
          <Box sx={{ ...VALUE_CHIP_SX, px: 0.7, minWidth: 42 }}>
            <Typography sx={{ fontSize: 13, fontWeight: 750, color: HEAT, letterSpacing: '-0.02em', lineHeight: 1 }}>
              {device.temperature != null ? Number(device.temperature).toFixed(1) : '—'}
              <Box component="span" sx={{ fontSize: 10, fontWeight: 600, ml: 0.1 }}>°</Box>
            </Typography>
          </Box>
          <Box sx={STEP_CHIP_SX}>
            <IconButton
              size="small"
              disabled={spDisabled}
              onClick={(e) => run(e, 'setpoint', (displaySp ?? 20) - step)}
              sx={STEP_BTN_SX}
              aria-label={t('evohome.card.setpointDown', { defaultValue: 'Bajar setpoint' })}
            >
              <RemoveIcon sx={{ fontSize: 15 }} />
            </IconButton>
            <Typography sx={STEP_VALUE_SX}>
              {displaySp != null ? (Number.isInteger(displaySp) ? displaySp : displaySp.toFixed(1)) : '—'}
              {displaySp != null && <Box component="span" sx={{ fontSize: 10, fontWeight: 600, ml: 0.1 }}>°</Box>}
            </Typography>
            <IconButton
              size="small"
              disabled={spDisabled}
              onClick={(e) => run(e, 'setpoint', (displaySp ?? 20) + step)}
              sx={STEP_BTN_SX}
              aria-label={t('evohome.card.setpointUp', { defaultValue: 'Subir setpoint' })}
            >
              <AddIcon sx={{ fontSize: 15 }} />
            </IconButton>
          </Box>
        </Box>
        {artefactoNombre ? (
          <Typography sx={artifactTypeLineSx}>{artefactoNombre}</Typography>
        ) : null}
      </Box>

      {offline && !isRoomEditing && (
        <Box sx={{ position: 'absolute', right: showFlame ? '76px' : '16px', bottom: 6, zIndex: 100 }}>
          <Typography sx={{
            fontSize: 10, fontWeight: 'bold', padding: '0px 4.4px', borderRadius: '8.2px',
            textAlign: 'center', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            whiteSpace: 'nowrap', lineHeight: 1.0, color: '#ff4444',
            backgroundColor: 'rgba(255, 68, 68, 0.1)', border: '1px solid #ff4444',
          }}>
            {t('devices.offline')}
          </Typography>
        </Box>
      )}

      {isRoomEditing && (
        <Box
          {...dragHandleProps}
          sx={{
            marginLeft: 0.5,
            transform: 'translateX(-10px)',
            flexShrink: 0,
            position: 'relative',
            zIndex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '40px',
            height: '40px',
            cursor: 'grab',
            pointerEvents: 'auto',
            touchAction: 'none',
            '&:active': { cursor: 'grabbing' },
          }}
        >
          <DragIndicator sx={{ color: 'white', fontSize: '2.25rem', pointerEvents: 'none' }} />
        </Box>
      )}

      {(Boolean(device.pin_required) || aiWhitelisted || aiBlacklisted) && (
        <Box sx={{
          position: 'absolute',
          right: showFlame ? '76px' : '16px',
          top: 12,
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          gap: '4px',
          zIndex: 5,
          pointerEvents: 'none',
        }}>
          {Boolean(device.pin_required) && <Lock sx={{ fontSize: 13, color: '#2391FF' }} />}
          {aiWhitelisted && (
            <Typography component="span" aria-label={t('deviceList.aiPolicyAllowed') as string} sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.62rem', fontWeight: 800, color: '#66bb6a', minHeight: 14, lineHeight: '14px', letterSpacing: '-0.02em', userSelect: 'none' }}>
              {t('common.aiBadge')}
            </Typography>
          )}
          {aiBlacklisted && (
            <Typography component="span" aria-label={t('deviceList.aiPolicyDenied') as string} sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.62rem', fontWeight: 800, color: '#ef5350', minHeight: 14, lineHeight: '14px', letterSpacing: '-0.02em', userSelect: 'none' }}>
              <Box component="span" sx={{ textDecoration: 'line-through', textDecorationColor: '#ef5350', textDecorationThickness: '1.5px' }}>
                {t('common.aiBadge')}
              </Box>
            </Typography>
          )}
        </Box>
      )}

      {showFlame && (
        <Box
          sx={{
            // Misma posición absoluta que Samsung/LG/Android TV alrededor de PowerButton.
            position: 'absolute',
            right: '10px',
            top: '50%',
            transform: 'translateY(-50%)',
            // Debajo del hit-area del cuerpo: el tap abre el modal (no es power accionable).
            zIndex: 0,
            pointerEvents: 'none',
            width: POWER_BUTTON_SIZE,
            height: POWER_BUTTON_SIZE,
          }}
        >
          <CardStatusPowerChrome
            active={askingHeat}
            accentColor={flameActiveColor}
            dimmed={offline}
            aria-label={askingHeat
              ? t('evohome.card.askingHeat', { defaultValue: 'Pidiendo calor' })
              : t('evohome.card.idleHeat', { defaultValue: 'Sin demanda de calor' })}
            icon={(
              <WhatshotIcon
                sx={{
                  fontSize: POWER_ICON_FONT_SIZE,
                  color: askingHeat ? flameActiveColor : '#fff',
                  display: 'block',
                  transition: 'color 0.3s ease-in-out',
                }}
              />
            )}
          />
        </Box>
      )}
    </Box>
  );
};

export default React.memo(EvohomeDeviceCard, (prev, next) => (
  prev.device.id === next.device.id
  && prev.device.temperature === next.device.temperature
  && prev.device.setpoint === next.device.setpoint
  && prev.device.online === next.device.online
  && prev.device.setpoint_mode === next.device.setpoint_mode
  && prev.device.asking_heat === next.device.asking_heat
  && prev.device.can_control === next.device.can_control
  && prev.device.pin_required === next.device.pin_required
  && prev.device.nombre === next.device.nombre
  && prev.device.artefacto_id === next.device.artefacto_id
  && prev.device.artefacto?.color_icono === next.device.artefacto?.color_icono
  && prev.device.missing_from_cloud === next.device.missing_from_cloud
  && prev.interactionLocked === next.interactionLocked
  && prev.editMode === next.editMode
  && prev.isRoomEditing === next.isRoomEditing
  && prev.isEditingName === next.isEditingName
  && prev.showBrandChip === next.showBrandChip
));
